import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, lazy, Suspense, ReactNode } from 'react';
import { useAuthStore } from './store/authStore';
import { useAdminStore } from './store/adminStore';
import { useUIStore } from './store/uiStore';
import { FeatureFlagsProvider, useFeatureFlags } from './context/FeatureFlagsContext';
import SsoAuthHandler from './components/SsoAuthHandler';
import ProfessionalSsoHandler from './components/ProfessionalSsoHandler';
import { redirectToIntus, intusBaseUrl } from './utils/intusRedirect';
import { perfilService } from './services/perfilService';
import DashboardPage from './pages/DashboardPage';
import EjerciciosPage from './pages/EjerciciosPage';
import ComunidadPage from './pages/ComunidadPage';
import VideosPage from './pages/VideosPage';

import PerfilPage from './pages/PerfilPage';
import MetasPage from './pages/MetasPage';
import NotificacionesPage from './pages/NotificacionesPage';
import InformePadresPage from './pages/InformePadresPage';
import MainLayout from './components/layout/MainLayout';
import WelcomePage from './pages/WelcomePage';
import { useAutoUpdateCheck } from './hooks/useAutoUpdateCheck';
import { DashboardSkeleton } from './components/dashboard/Estados';
import { ShieldOff } from 'lucide-react';

/**
 * Code splitting por ruta (P1 del plan de cierre).
 *
 * Antes: las 20 rutas se importaban estaticamente, asi que el bundle inicial
 * cargaba TODO. Medido: 268,7 kB gzip, de los que 109,6 kB eran `recharts`.
 *
 * `recharts` lo usa UNICAMENTE `components/admin/UsageChart.tsx`, que a su vez
 * solo lo usa `pages/admin/AdminDashboardPage.tsx`. Con la pagina del panel
 * admin fuera del entry, el estudiante deja de descargar la libreria de
 * graficas: el panel admin la pide al entrar, que es donde la usa.
 *
 * Se empieza por ahi a proposito, no por todas las rutas: es el cambio de mayor
 * rendimiento y el de menor riesgo (el panel admin es el unico que puede
 * romperse, y se comprueba entrando en el). El resto se agrupa despues, medido.
 *
 * `manualChunks` en vite.config.ts sigue partiendo `charts` en su propio chunk,
 * pero eso solo sirve si el modulo deja de ser dependencia ESTATICA del entry.
 */
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));

/**
 * Juegos arrastra 11 componentes de juego (CalmaMatch, PuzzleZen, MenteGuerrera,
 * OrdenaZen, ArteEmocional y siete mas) de golpe. Es la pagina mas pesada del
 * producto y no es la de entrada, asi que se carga entera al abrirla y no antes.
 * Un solo limite para los 11: trocearlos uno a uno generaria once chunks
 * pequenos sin ganancia real.
 */
const JuegosPage = lazy(() => import('./pages/JuegosPage'));

/**
 * Paginas restringidas a PSICOLOGO. Un estudiante no las abre nunca, asi que
 * cargar su codigo en la primera visita es peso muerto para el caso mayoritario.
 * El psicologo las paga una vez, al entrar: que es donde las usa.
 */
const DashboardPsicologoPage = lazy(() => import('./pages/DashboardPsicologoPage'));
const PacienteDetallePage = lazy(() => import('./pages/PacienteDetallePage'));
const AlertasPsicologoPage = lazy(() => import('./pages/AlertasPsicologoPage'));
const CrearCuestionarioPage = lazy(() => import('./pages/CrearCuestionarioPage'));

/**
 * Un ejercicio concreto (`/ejercicios/:id?min=3`). Se llega desde la lista con
 * un tap, nunca de entrada, asi que tampoco entra en el bundle inicial: abrir
 * la lista no descarga el cronometro, el circulo ni la guia sonora.
 */
const EjercicioDetallePage = lazy(() => import('./pages/EjercicioDetallePage'));

/**
 * Evaluacion va en un solo grupo por una razon concreta: la pantalla de
 * resultado solo se alcanza terminando un cuestionario, asi que casi siempre se
 * piden las dos seguidas. Separarlas no ahorra nada y anade una segunda ida al
 * servidor en el momento en que el estudiante esta esperando el resultado.
 */
const EvaluacionPage = lazy(() => import('./pages/EvaluacionPage'));
const ResultadoEvaluacionPage = lazy(() => import('./pages/ResultadoEvaluacionPage'));

/**
 * Envoltura de las rutas con carga diferida.
 *
 * El fallback reutiliza `DashboardSkeleton`, que ya esta en el bundle inicial
 * porque el dashboard es una ruta eager: no cuesta bytes y tiene la forma del
 * dashboard final, asi que no hay CLS al cambiar de estado.
 *
 * NO envuelve a los hijos en ningun contenedor con padding ni `max-w-*`: cada
 * pagina ya trae el suyo yjzarla con uno aqui duplicaria el ancho y anadiria
 * padding, cambiando el layout de la pagina real al terminar de cargar.
 *
 * Si se anade una ruta lazy nueva, se mete dentro de `<RutaDiferida>`: un
 * `lazy()` sin `<Suspense>` que lo enclose revienta la app.
 */
function RutaDiferida({ children }: { children: ReactNode }) {
  return <Suspense fallback={<DashboardSkeleton />}>{children}</Suspense>;
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  const hasSsoToken =
    new URLSearchParams(window.location.search).has('token') ||
    new URLSearchParams(window.location.search).has('professional_token');

  // SSO en curso o sin sesión: NO exponer el login admin (es secreto).
  if (!isAuthenticated) {
    if (isLoading || hasSsoToken) {
      return null;
    }
    return <IntusRedirect />;
  }

  return <MainLayout>{children}</MainLayout>;
}

/**
 * Ruta de la bienvenida de Vita360 App: pantalla completa SIN MainLayout
 * (apariencia de app nativa). Requiere sesión, igual que el resto.
 */
function WelcomeRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  const hasSsoToken =
    new URLSearchParams(window.location.search).has('token') ||
    new URLSearchParams(window.location.search).has('professional_token');

  if (!isAuthenticated) {
    if (isLoading || hasSsoToken) {
      return null;
    }
    return <IntusRedirect />;
  }

  return <>{children}</>;
}

/**
 * Redirige al portal paciente de Vita360 (bienvenida). Sin sesión SSO no hay
 * redirección: se muestra un aviso neutro (el login admin queda oculto).
 * Si hay ?token= en la URL se espera a que SsoAuthHandler lo intercambie
 * antes de redirigir (evita pantalla en blanco por race condition).
 */
function IntusRedirect() {
  const hasSsoToken = new URLSearchParams(window.location.search).has('token');
  const hasProfessionalToken = new URLSearchParams(window.location.search).has('professional_token');
  const { isLoading } = useAuthStore();

  useEffect(() => {
    if (hasSsoToken || hasProfessionalToken || isLoading) {
      return;
    }
    redirectToIntus();
  }, [hasSsoToken, hasProfessionalToken, isLoading]);

  // Mientras hay token SSO (SsoAuthHandler procesándolo) o la sesión carga,
  // se muestra nada (evita pantalla en blanco permanente y redirección prematura).
  if (hasSsoToken || hasProfessionalToken || isLoading) {
    return null;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-sm text-v-on-surface-variant">
        Tu sesión expiró. Ingresa nuevamente desde el portal de Vita360.
      </p>
      <a
        href={intusBaseUrl()}
        className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:brightness-105"
      >
        Ir al portal de Vita360
      </a>
    </div>
  );
}

function RoleBasedRoute({ children, allowedRoles }: { children: ReactNode; allowedRoles: string[] }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  const hasSsoToken =
    new URLSearchParams(window.location.search).has('token') ||
    new URLSearchParams(window.location.search).has('professional_token');

  if (!isAuthenticated) {
    if (isLoading || hasSsoToken) {
      return null;
    }
    return <IntusRedirect />;
  }

  if (!allowedRoles.includes(user?.rol || '')) {
    return <Navigate to="/dashboard" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
}

/** Ruta del panel Admin: usa su propio store y layout (sin MainLayout). */
function AdminRoute({ children }: { children: ReactNode }) {
  const { isAdminAuthenticated } = useAdminStore();

  if (!isAdminAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Bloquea el acceso a un área si su feature flag está deshabilitada desde el
 * panel Admin (o desde el Superadmin INTUS, que sincroniza los flags).
 */
function FeatureGate({ feature, children }: { feature: string; children: ReactNode }) {
  const { isEnabled, isLoading } = useFeatureFlags();
  const navigate = useNavigate();

  if (isLoading) return null;

  if (!isEnabled(feature)) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-6 text-center">
        <ShieldOff className="h-12 w-12 text-v-outline" />
        <h2 className="text-lg font-semibold text-v-on-surface">Área deshabilitada</h2>
        <p className="max-w-sm text-sm text-v-on-surface-variant">
          Esta sección está temporalmente desactivada. Vuelve más tarde o consulta con tu profesional.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Registra el service worker y suscribe el navegador a Web Push (Fase 6.2).
 * Solo actúa si VITE_VAPID_PUBLIC_KEY está configurada en deploy; sin la
 * clave no hace nada (los VAPID_* son secretos de deploy).
 */
function PushSubscriptionManager() {
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const publicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

    if (
      !publicKey ||
      !isAuthenticated ||
      typeof window === 'undefined' ||
      !('serviceWorker' in navigator) ||
      !('PushManager' in window)
    ) {
      return;
    }

    // TS 5.7+ parametriza `Uint8Array` por su buffer. `applicationServerKey` de
    // la API de Push espera `BufferSource` sobre `ArrayBuffer` concreto (no
    // `ArrayBufferLike`), asi que se construye sobre un `ArrayBuffer` propio
    // en lugar del spread de `rawData`.
    const urlBase64ToUint8Array = (base64String: string): Uint8Array<ArrayBuffer> => {
      const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);

      const bytes = new Uint8Array(new ArrayBuffer(rawData.length));
      for (let i = 0; i < rawData.length; i += 1) {
        bytes[i] = rawData.charCodeAt(i);
      }
      return bytes;
    };

    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => registration.pushManager.getSubscription())
      .then((existing) => {
        if (existing) {
          return existing;
        }

        return navigator.serviceWorker.ready.then((registration) =>
          registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          }),
        );
      })
      .then((subscription) => {
        if (subscription) {
          return perfilService.guardarSuscripcionPush(subscription.toJSON() as any);
        }
      })
      .catch((error) => console.error('Push subscription failed:', error));
  }, [isAuthenticated]);

  return null;
}

/**
 * Aplica el tema al elemento raiz, no a un div del layout.
 *
 * POR QUE EN `<html>` Y NO EN UN DIV
 * ---------------------------------
 * `body` lleva `@apply bg-background text-foreground`, y ese color se CALCULA
 * una sola vez con los tokens de `:root`. Los descendientes heredan el color ya
 * resuelto, no el token.
 *
 * Poner `.dark` en un div interior cambiaba los tokens de los hijos, pero el
 * texto SIN clase de color propia —los `h1`, los `p`— seguia heredando el casi
 * negro de `:root`. El sintoma medido: los titulos salian a **1.14:1** sobre el
 * fondo oscuro, y `text-muted-foreground` (128 usos) tampoco llegaba a leerse.
 *
 * Aqui, y no en `MainLayout`, porque las rutas que no pasan por el layout —login
 * admin, el panel, las pantallas de error— tambien necesitan el tema.
 */
function AplicadorDeTema() {
  const theme = useUIStore((s) => s.theme);

  useEffect(() => {
    const raiz = document.documentElement;

    if (theme === 'dark') raiz.classList.add('dark');
    else raiz.classList.remove('dark');
  }, [theme]);

  return null;
}

function App() {
  // Recarga la pestaña si el servidor sirve una versión nueva del frontend.
  useAutoUpdateCheck();

  return (
    <BrowserRouter>
      <AplicadorDeTema />
      <FeatureFlagsProvider>
        <SsoAuthHandler />
        <ProfessionalSsoHandler />
        <PushSubscriptionManager />
        <Routes>
          {/* Los pacientes entran SOLO vía INTUS (SSO ?token=). No existe
              login/registro público de pacientes: /login, /registro y / no
              exponen el login admin (secreto) y redirigen al portal INTUS. */}
          <Route path="/login" element={<IntusRedirect />} />
          <Route path="/registro" element={<IntusRedirect />} />

          {/* Panel Admin (login público + dashboard protegido) */}
          <Route
            path="/admin/login"
            element={
              <RutaDiferida>
                <AdminLoginPage />
              </RutaDiferida>
            }
          />
          <Route
            path="/admin"
            element={
              <RutaDiferida>
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              </RutaDiferida>
            }
          />

          {/* Bienvenida a Vita360 App (destino post-SSO, sin MainLayout) */}
          <Route
            path="/bienvenida"
            element={
              <WelcomeRoute>
                <WelcomePage />
              </WelcomeRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard-psicologo"
            element={
              <RoleBasedRoute allowedRoles={['PSICOLOGO']}>
                <RutaDiferida>
                  <DashboardPsicologoPage />
                </RutaDiferida>
              </RoleBasedRoute>
            }
          />
          <Route
            path="/dashboard-psicologo/paciente/:id"
            element={
              <RoleBasedRoute allowedRoles={['PSICOLOGO']}>
                <RutaDiferida>
                  <PacienteDetallePage />
                </RutaDiferida>
              </RoleBasedRoute>
            }
          />
          <Route
            path="/evaluacion"
            element={
              <ProtectedRoute>
                <FeatureGate feature="evaluaciones">
                  <RutaDiferida>
                    <EvaluacionPage />
                  </RutaDiferida>
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/evaluacion/resultado/:id"
            element={
              <ProtectedRoute>
                <FeatureGate feature="evaluaciones">
                  <RutaDiferida>
                    <ResultadoEvaluacionPage />
                  </RutaDiferida>
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ejercicios"
            element={
              <ProtectedRoute>
                <FeatureGate feature="ejercicios_juegos">
                  <EjerciciosPage />
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          {/*
            Antes el ejercicio se pintaba dentro de `/ejercicios` y por eso no
            existia esta ruta. Va DESPUES de `/ejercicios`: en v6 el orden no
            importa (`/ejercicios` no matchea `/ejercicios/1`), pero mantener el
            par junto hace obvio que son la lista y su detalle.
          */}
          <Route
            path="/ejercicios/:id"
            element={
              <ProtectedRoute>
                <FeatureGate feature="ejercicios_juegos">
                  <RutaDiferida>
                    <EjercicioDetallePage />
                  </RutaDiferida>
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/comunidad"
            element={
              <ProtectedRoute>
                <FeatureGate feature="comunidad">
                  <ComunidadPage />
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/videos"
            element={
              <ProtectedRoute>
                <FeatureGate feature="videos">
                  <VideosPage />
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/juegos"
            element={
              <ProtectedRoute>
                <FeatureGate feature="ejercicios_juegos">
                  <RutaDiferida>
                    <JuegosPage />
                  </RutaDiferida>
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <PerfilPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/metas"
            element={
              <ProtectedRoute>
                <MetasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notificaciones"
            element={
              <ProtectedRoute>
                <NotificacionesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/informe-padres"
            element={
              <ProtectedRoute>
                <FeatureGate feature="informes">
                  <InformePadresPage />
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/alertas-psicologo"
            element={
              <RoleBasedRoute allowedRoles={['PSICOLOGO']}>
                <RutaDiferida>
                  <AlertasPsicologoPage />
                </RutaDiferida>
              </RoleBasedRoute>
            }
          />
          <Route
            path="/crear-cuestionario"
            element={
              <RoleBasedRoute allowedRoles={['PSICOLOGO']}>
                <FeatureGate feature="evaluaciones">
                  <RutaDiferida>
                    <CrearCuestionarioPage />
                  </RutaDiferida>
                </FeatureGate>
              </RoleBasedRoute>
            }
          />
          {/* Default route: si hay token SSO en la URL, se deja que SsoAuthHandler /
              ProfessionalSsoHandler lo procese antes de redirigir al portal (evita pantalla en blanco) */}
          <Route path="/" element={<IntusRedirect />} />

          {/* 404 route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </FeatureFlagsProvider>
    </BrowserRouter>
  );
}

export default App;
